/**
 * Salary role page island loader: hydrates the server-rendered tree in place (indexable-page
 * hydration rework, following the SRP/job/salary-hub fix). layouts/LayoutDefault.tsx's
 * salary-role branch now server-renders SalaryRolePage wrapped in the SAME full provider stack
 * this file builds (Provider/store, AuthProvider, FeedbackModalProvider, ToastProvider,
 * SearchProvider, SearchWrapper) directly into #salary-role-ssr — there's no separate
 * #salary-role-app to mount a new tree into and swap in anymore, so this file just calls
 * hydrateRoot() on #salary-role-ssr.
 */
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import type { PageContextClient } from 'vike/types';

import { getReactRootSentryOptions } from '../../../utils/react/react-root-sentry-options';
import { loadLocaleData } from '../../../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../../../utils/footer/buildFooterStaticStrings';
import { createStore } from '../../../redux/createStore';
import { AuthProvider } from '../../../contexts/AuthContext';
import { ExperimentProvider } from '../../../contexts/ExperimentContext';
import { UserProvider } from '../../../query/UserProvider';
import { FeedbackModalProvider } from '../../../contexts/FeedbackModalContext';
import { ToastProvider } from '../../../contexts/ToastContext';
import { FooterStringsProvider } from '../../../contexts/FooterStringsContext';
import { ToastContainer } from '../../../design-system/Toast';
import { SearchProvider } from '../../../contexts/SearchContext';
import { SearchWrapper } from '../../../layouts/SearchWrapper';
import { getIndexName } from '../../../constants';
import {
  scheduleIdleCallbackWithFallback,
  cancelIdleScheduleHandle,
  type IdleScheduleHandle,
} from '../../../utils/browser/scheduleIdleCallbackWithFallback';

/** Worst-case cap (ms) for the deferred hydrate path — see srp.client.ts's constant of the same name. */
const DEFER_MOUNT_FALLBACK_MS = 2500;

let salaryRoleRoot: Root | null = null;
let salaryRoleRootContainer: HTMLElement | null = null;

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;

function getLocaleFromPathname(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first) ? first : 'en';
}

/**
 * Unmount the salary-role island root synchronously. Must be called by `+onRenderClient.ts`
 * before it creates/renders a *different* React root over `#root` (an ancestor of
 * `#salary-role-ssr`) — same ordering reason as the other island loaders.
 */
export function unmountSalaryRoleIsland(): void {
  if (salaryRoleRoot) {
    salaryRoleRoot.unmount();
    salaryRoleRoot = null;
    salaryRoleRootContainer = null;
  }
}

export interface RunSalaryRoleIslandLoaderOptions {
  /** When true (e.g. we skipped root hydration), hydrate immediately instead of deferring. */
  mountImmediately?: boolean;
}

async function hydrateSalaryRoleIsland(container: HTMLElement, pageContext: PageContextClient): Promise<void> {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ((pageContext as any)?.urlPathname ?? '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || 'en';
  const messages = loadLocaleData(locale);
  const footerStrings = buildFooterStaticStrings(messages);
  const store = createStore(pageContext);
  const initialUser = (pageContext?.user ?? null) as any;
  const initialHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  const initialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};

  const { default: SalaryRolePage } = await import('./+Page');

  const pc = pageContext as { userState?: Record<string, unknown> } | undefined;
  const initialUserState = (pc?.userState && typeof pc.userState === 'object' ? pc.userState : {}) as any;

  const tree = React.createElement(
    Provider,
    { store },
    React.createElement(
      IntlProvider,
      { messages, locale, defaultLocale: 'en' },
      React.createElement(
        FooterStringsProvider,
        { strings: footerStrings },
        React.createElement(
          AuthProvider,
          { initialUser, initialHasBackofficeAccess },
          React.createElement(
            ExperimentProvider,
            { initialVariants: initialExperiments },
          // Needed for RoleFollowButton's useUserState()/useRoleFollowActions() (TEC-404's
          // TanStack layer) — without this the salary-role island crashes as soon as it mounts,
          // since those hooks throw outside UserProvider. Mirrors job.client.ts's/SrpApp.tsx's
          // own UserProvider wiring, including sharing the same QueryClient singleton so a
          // follow/unfollow here is visible from the SRP/job-detail islands too.
          React.createElement(
            UserProvider,
            { initialUser, initialHasBackofficeAccess, initialUserState },
            React.createElement(
              FeedbackModalProvider,
              null,
              React.createElement(
                ToastProvider,
                null,
                React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(ToastContainer),
                  React.createElement(
                    SearchProvider,
                    {
                      indexName: getIndexName(locale),
                      initialQuery: '',
                      initialFilters: {},
                      initialPage: 0,
                      initialResults: null,
                      initialSort: 'relevance',
                      syncToUrl: false,
                      localeOverride: locale,
                    },
                    React.createElement(SearchWrapper, { isSearchPage: false }, React.createElement(SalaryRolePage)),
                  ),
                ),
              ),
            ),
          ),
          ),
        ),
      ),
    ),
  );

  if (salaryRoleRoot && salaryRoleRootContainer === container) {
    salaryRoleRoot.render(tree);
    return;
  }

  if (salaryRoleRoot && salaryRoleRootContainer) {
    salaryRoleRoot.unmount();
    salaryRoleRoot = null;
    salaryRoleRootContainer = null;
  }

  salaryRoleRoot = hydrateRoot(container, tree, getReactRootSentryOptions());
  salaryRoleRootContainer = container;
}

export function runSalaryRoleIslandLoader(pageContext: PageContextClient, options: RunSalaryRoleIslandLoaderOptions = {}): void {
  const appEl = document.getElementById('salary-role-ssr');
  if (!appEl) return;

  let mounted = false;
  let fallbackHandle: IdleScheduleHandle | null = null;

  const doMount = (): void => {
    if (mounted) return;
    mounted = true;
    if (fallbackHandle !== null) {
      cancelIdleScheduleHandle(fallbackHandle);
      fallbackHandle = null;
    }
    void hydrateSalaryRoleIsland(appEl, pageContext);
  };

  const onInteraction = (): void => {
    doMount();
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
    document.removeEventListener('focus', onInteraction, true);
  };

  document.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
  document.addEventListener('keydown', onInteraction, { once: true, passive: true });
  document.addEventListener('focus', onInteraction, { once: true, capture: true });

  if (options.mountImmediately) {
    doMount();
  } else {
    fallbackHandle = scheduleIdleCallbackWithFallback(doMount, { timeout: DEFER_MOUNT_FALLBACK_MS });
  }
}
